import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Invoice, InvoiceStatus } from '../../types';

interface InvoiceUpdateDialogProps {
  open: boolean;
  invoice: Invoice | null;
  userRole?: string;
  status: InvoiceStatus;
  reviewerComments: string;
  overrideTotal: string;
  onOpenChange: (open: boolean) => void;
  onStatusChange: (status: InvoiceStatus) => void;
  onCommentsChange: (comments: string) => void;
  onOverrideTotalChange: (total: string) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
}

export const InvoiceUpdateDialog: React.FC<InvoiceUpdateDialogProps> = ({
  open,
  invoice,
  userRole,
  status,
  reviewerComments,
  overrideTotal,
  onOpenChange,
  onStatusChange,
  onCommentsChange,
  onOverrideTotalChange,
  onSubmit,
  isSubmitting = false,
}) => {
  const { t } = useTranslation('invoices');
  const { t: tCommon } = useTranslation('common');
  if (!invoice) return null;

  const isUser = userRole === 'user';
  const isAdmin = userRole === 'admin';
  const isManager = userRole === 'manager';
  const canChangeStatus = isAdmin || isManager;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isUser ? t('update.titleNotes') : t('update.title')}
          </DialogTitle>
          <DialogDescription>
            {isUser
              ? t('update.descriptionNotes')
              : t('update.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>{t('update.invoiceDetails')}</Label>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>
                <span className="font-medium">{t('table.headers.month')}:</span> {invoice.invoiceMonth}
              </p>
              <p>
                <span className="font-medium">{t('table.headers.employee')}:</span> {invoice.userName}
              </p>
              <p>
                <span className="font-medium">{t('table.headers.amount')}:</span>{' '}
                {invoice.overrideTotal != null
                  ? `$${invoice.overrideTotal.toFixed(2)} (${tCommon('override')})`
                  : invoice.extractedInvoiceTotal != null
                  ? `$${invoice.extractedInvoiceTotal.toFixed(2)}`
                  : tCommon('notExtracted')}
              </p>
            </div>
          </div>

          {canChangeStatus && (
            <div className="grid gap-2">
              <Label htmlFor="status">{t('update.status')}</Label>
              <Select value={status} onValueChange={(value) => onStatusChange(value as InvoiceStatus)}>
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={InvoiceStatus.PENDING}>{tCommon('status.pending')}</SelectItem>
                  <SelectItem value={InvoiceStatus.UNDER_REVIEW}>{tCommon('status.underReview')}</SelectItem>
                  <SelectItem value={InvoiceStatus.APPROVED}>{tCommon('status.approved')}</SelectItem>
                  <SelectItem value={InvoiceStatus.REJECTED}>{tCommon('status.rejected')}</SelectItem>
                  {isAdmin && (
                    <SelectItem value={InvoiceStatus.PAID}>{tCommon('status.paid')}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {isAdmin && (
            <div className="grid gap-2">
              <Label htmlFor="override-total">{t('update.overrideTotal.label')}</Label>
              <Input
                id="override-total"
                type="number"
                step="0.01"
                placeholder={t('update.overrideTotal.placeholder')}
                value={overrideTotal}
                onChange={(e) => onOverrideTotalChange(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t('update.overrideTotal.hint')}
              </p>
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="comments">
              {!canChangeStatus
                ? t('update.notes')
                : status === InvoiceStatus.REJECTED
                ? `${t('update.reviewerComments')} (${t('statusChangeNote.required')})`
                : status === InvoiceStatus.UNDER_REVIEW
                ? `${t('update.reviewerComments')} (${t('statusChangeNote.recommended')})`
                : t('update.reviewerComments')}
            </Label>
            <Textarea
              id="comments"
              placeholder={
                isUser
                  ? t('update.notesPlaceholder')
                  : status === InvoiceStatus.REJECTED
                  ? t('statusChangeNote.placeholderRequired')
                  : status === InvoiceStatus.UNDER_REVIEW
                  ? t('statusChangeNote.placeholderOptional')
                  : t('update.commentsPlaceholder')
              }
              value={reviewerComments}
              onChange={(e) => onCommentsChange(e.target.value)}
              rows={4}
            />
            {canChangeStatus && status === InvoiceStatus.REJECTED && reviewerComments.trim().length === 0 && (
              <p className="text-xs text-destructive">
                {t('statusChangeNote.validationRequired')}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {tCommon('buttons.cancel')}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={
              isSubmitting ||
              (canChangeStatus && status === InvoiceStatus.REJECTED && reviewerComments.trim().length === 0)
            }
          >
            {isSubmitting ? tCommon('saving') : tCommon('buttons.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
